import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { object, string, boolean, array } from 'yup';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import { ROLES } from '../../constants/roles';

// Components
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../components/InputControl/InputControl';
import SelectGroupOne from '../../components/Forms/SelectGroup/SelectGroupOne';
import SwitcherOne from '../../components/Switchers/SwitcherOne';
import MultiSelectOption from '../../components/MultiSelectOption/MultiSelectOption';
import { Action } from '../../enums/action';
import { ACTION_BY_PAGE, PAGE_TITLE } from '../../constants/common';
import defineAbility from '../../ability/defineAbility';
import { Can } from '@casl/react';
import { Location } from '../../store/models/locations';
import useSafeRequest from '../../hooks/useSafeRequest';
import { GenericAbortSignal } from 'axios';

type EditMemberProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

type FormValues = {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: string | null;
  active: boolean | null;
  locations: Location[];
};

const EditMember: React.FC<EditMemberProps> = ({
  member,
  locations,
  getLocations,
  getMember,
  updateMember,
  resetMember,
  currentUser,
}) => {
  const { id } = useParams();
  const ability = defineAbility(currentUser, member);

  const [isMemberLoaded, setIsMemberLoaded] = useState(false);

  let options = Object.values(ROLES).map((role) => ({
    value: role,
    label: role,
    disabled: false,
  }));

  options = [
    { value: '', label: 'Choose member role', disabled: true },
    ...options,
  ];

  if (!options.some((option) => option.value === member?.role)) {
    options = [
      ...options,
      { value: member?.role, label: member?.role, disabled: false },
    ];
  }

  const formik = useFormik<FormValues>({
    initialValues: {
      first_name: null,
      last_name: null,
      email: null,
      role: null,
      active: null,
      locations: [],
    },
    validationSchema: object({
      role: string()
        .oneOf(Object.values(ROLES))
        .required(),
      active: boolean().required(),
      locations: array(),
    }),
    onSubmit: async (values) => {
      const { role, active, locations } = values;
      const locationIds = locations.map((location) => location.id);
      await updateMember({ id, formData: { role, active, locationIds } });
    },
  });

  const isDisabledBtn = !(Object.keys(formik.touched).length > 0 && Object.keys(formik.errors).length === 0);

  const fetchData = async (params: { memberId: string, signal?: GenericAbortSignal }) => {
    const [response] = await Promise.all([
      getMember(params),
      getLocations(),
    ]);

    const controllerData = response?.member;
    if (controllerData) {
      const formikValueKeys = Object.keys(formik.initialValues);
      Object.entries(controllerData).forEach(([key, value]) => {
        const isExistKey = formikValueKeys.some((item) => item === key);

        if (isExistKey) {
          formik.handleChange({
            target: {
              name: key,
              value: value,
            },
          });
        }
      });
    }
    setIsMemberLoaded(true);
  };

  const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    formik.setFieldTouched(name, true);

    formik.handleChange({
      target: { name, value },
    });
  };

  const getOptions = (data: Location[]) => {
    const list = (data && data.length !== 0) ? data.map((location) => ({ id: location.id, value: location.name })) : [];
    return list;
  }

  // The dropdown's own options are scoped to locations the acting Supervisor
  // has access to, but the member being edited may already be assigned to a
  // location outside that set. Without it in the list, MultiSelectOption's
  // selected-index lookup silently fails for that location (finds no match,
  // falls back to index -1), which corrupts the submitted location list and
  // either crashes on save or drops the member's existing access.
  const assignableLocationOptions = useMemo(() => {
    const ownOptions = getOptions(locations);
    const memberOptions = getOptions(member?.locations ?? []);
    const byId = new Map(ownOptions.map((option) => [option.id, option]));

    memberOptions.forEach((option) => {
      if (!byId.has(option.id)) {
        byId.set(option.id, option);
      }
    });

    return Array.from(byId.values());
  }, [locations, member]);

  const selectLocations = (data: { name: string, selectedOptions: any[] }) => {
    formik.handleChange({
      target: { name: data.name, value: data.selectedOptions },
    });
  }

  const touchLocationField = () => {
    formik.setFieldTouched('locations', true);
  };

  const resetData = () => {
    resetMember();
    formik.resetForm();
  }

  useSafeRequest({
    request: fetchData,
    params: { memberId: id },
    options: {
      deps: [id],
      reset: resetData,
    }
  });

  return (
    <>
      {!isMemberLoaded && (
        <div className="flex items-center justify-center">Loading...</div>
      )}
      {isMemberLoaded && (
        <div className="p-edit-member pb-4 md:pb-25">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 className="font-medium text-black dark:text-white">
                Edit Member
              </h3>
            </div>
            <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
              <div className="w-full">
                <InputControl
                  label="Email (not edited)"
                  type="text"
                  value={formik.values?.email}
                  disabled
                />
              </div>
              <div className="w-full">
                <InputControl
                  label="First Name (not edited)"
                  type="text"
                  value={formik.values.first_name}
                  disabled
                />
              </div>

              <div className="w-full">
                <InputControl
                  label="Last Name (not edited)"
                  type="text"
                  value={formik.values.last_name}
                  disabled
                />
              </div>

              <div className="w-full">
                <Can I={Action.Update} a={ACTION_BY_PAGE[PAGE_TITLE.EDIT_MEMBER].CHANGE_ROLE} passThrough ability={ability}>
                  {allowed => (
                    <SelectGroupOne
                      label="Role"
                      name='role'
                      handleChange={handleSelect}
                      options={options}
                      selectedOption={formik.values.role}
                      disabled={(!allowed)}
                      showError={formik.touched.role && formik.errors.role}
                      errorText={formik.errors.role}
                    />
                  )}
                </Can>
              </div>

              <Can I={Action.Update} a={ACTION_BY_PAGE[PAGE_TITLE.EDIT_MEMBER].CHANGE_LOCATIONS} passThrough ability={ability}>
                {allowed => (
                  <MultiSelectOption
                    updateValues={selectLocations}
                    name="locations"
                    title='Multiselect clients'
                    listOptions={assignableLocationOptions}
                    selectedOptions={getOptions(member.locations)}
                    disabled={(!allowed)}
                    errorText={`${formik.errors?.locations}`}
                    showError={Boolean(formik.errors.locations && formik.errors.locations)}
                    onClose={touchLocationField}
                  />
                )}
              </Can>

              <Can I={Action.Update} a={ACTION_BY_PAGE[PAGE_TITLE.EDIT_MEMBER].CHANGE_ACTIVE_STATUS} passThrough ability={ability}>
                {allowed => (
                  <SwitcherOne
                    name="active"
                    enabled={formik.values.active}
                    handleChange={formik.handleChange}
                    label={formik.values.active ? 'Activated' : 'Deactivated'}
                    disabled={!allowed}
                  />
                )}
              </Can>

              <div className="flex justify-end gap-4.5">
                <InteractiveButton
                  text="Update"
                  submit
                  disabled={isDisabledBtn}
                  isLoading={formik.isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  member: state.members.member,
  locations: state.locations.locations,
  currentUser: state.user.profile,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getMember: dispatch.members.getMember,
  getLocations: dispatch.locations.getLocations,
  updateMember: dispatch.members.updateMember,
  resetMember: dispatch.members.resetMember,
});

export default connect(mapState, mapDispatch)(EditMember);
